import React, { useState, useEffect } from 'react';
import { Users, MapPin, Phone, Mail, Globe, Star, Heart, CreditCard, UserPlus, Building2, DollarSign, TrendingUp, Shield, FileText, Calendar, UserCheck, AlertTriangle, TestTube, X, Save } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import MasterDataService from '../../services/masterDataService';
import CustomerService from '../../services/customerService';
import { useToast, ToastContainer } from '../common/Toast';

const CustomerForm = ({ customer, onSave, onCancel, viewMode = false }) => {
  const { user, company } = useAuth();
  const { toasts, success, error, removeToast } = useToast();
  const [masterData, setMasterData] = useState({
    paymentTerms: [],
    currencies: []
  });
  const [loadingMasterData, setLoadingMasterData] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [testDataLoaded, setTestDataLoaded] = useState(false);
  
  const [formData, setFormData] = useState({
    // Basic Information
    name: '',
    legalName: '',
    tradingName: '',
    customerType: 'Business',
    customerTier: 'Standard',
    industry: '',
    sector: '',
    
    // Business Registration
    businessRegistration: {
      taxId: '',
      registrationNumber: '',
      legalEntityType: 'Corporation',
      incorporationDate: '',
      incorporationCountry: ''
    },
    
    // Primary Contact
    primaryContact: {
      name: '',
      title: '',
      email: '',
      phone: '',
      mobile: ''
    },
    
    // Decision Makers
    decisionMakers: [],
    
    // Addresses
    addresses: {
      headquarters: {
        street: '',
        city: '',
        state: '',
        postalCode: '',
        country: ''
      },
      billing: {
        street: '',
        city: '',
        state: '',
        postalCode: '',
        country: ''
      },
      shipping: {
        street: '',
        city: '',
        state: '',
        postalCode: '',
        country: ''
      }
    },
    
    // Service Locations
    serviceLocations: [],
    
    // Communication
    communication: {
      website: '',
      email: '',
      phone: '',
      fax: '',
      preferredMethod: 'Email',
      communicationPreferences: {
        marketing: true,
        serviceUpdates: true,
        billing: true,
        emergency: true
      }
    },
    
    // Business Details
    businessDetails: {
      creditLimit: 0,
      paymentTerms: 'Net 30',
      currency: 'USD',
      taxExempt: false,
      taxExemptionNumber: '',
      annualRevenue: '',
      employeeCount: ''
    },
    
    // Banking Information
    banking: {
      bankName: '',
      accountNumber: '',
      routingNumber: '',
      swiftCode: '',
      iban: ''
    },
    
    // Customer Value
    customerValue: {
      lifetimeValue: 0,
      totalSpent: 0,
      averageOrderValue: 0,
      orderFrequency: 0,
      lastPurchaseDate: '',
      firstPurchaseDate: '',
      profitability: 0
    },
    
    // Service Interests
    serviceInterests: [],
    
    // Satisfaction
    satisfaction: {
      overallRating: 3,
      serviceRating: 3,
      productRating: 3,
      supportRating: 3,
      lastSatisfactionSurvey: '',
      feedback: '',
      npsScore: ''
    },
    
    // Risk Assessment
    riskAssessment: {
      riskLevel: 'Medium',
      creditScore: '',
      paymentHistory: {
        onTime: 0,
        late: 0,
        defaulted: 0
      },
      riskFactors: [],
      lastRiskReview: ''
    },
    
    // Portal Access
    portalAccess: {
      hasAccess: false,
      username: '',
      lastLogin: '',
      accessLevel: 'Basic',
      preferences: {
        theme: 'light',
        language: 'en',
        notifications: true
      }
    },
    
    // Status
    status: 'Active'
  });

  const [activeTab, setActiveTab] = useState('basic');
  const [newDecisionMaker, setNewDecisionMaker] = useState({
    name: '',
    title: '',
    role: 'Decision Maker',
    email: '',
    phone: '',
    isPrimary: false
  });
  const [newServiceLocation, setNewServiceLocation] = useState({
    name: '',
    address: {
      street: '',
      city: '',
      state: '',
      postalCode: '',
      country: ''
    },
    contactPerson: {
      name: '',
      phone: '',
      email: ''
    },
    isPrimary: false
  });
  const [newServiceInterest, setNewServiceInterest] = useState({
    category: '',
    subcategory: '',
    priority: 'Medium',
    budget: '',
    timeline: ''
  });
  const [newRiskFactor, setNewRiskFactor] = useState({
    factor: '',
    impact: 'Medium',
    mitigation: ''
  });

  useEffect(() => {
    if (customer) {
      // Initialize form data with customer data, using default values for missing fields
      const initialFormData = {
        // Basic Information
        name: customer.name || '',
        legalName: customer.legalName || '',
        tradingName: customer.tradingName || '',
        customerType: customer.customerType || 'Business',
        customerTier: customer.customerTier || 'Standard',
        industry: customer.industry || '',
        sector: customer.sector || '',
        
        // Business Registration
        businessRegistration: {
          taxId: customer.businessRegistration?.taxId || '',
          registrationNumber: customer.businessRegistration?.registrationNumber || '',
          legalEntityType: customer.businessRegistration?.legalEntityType || 'Corporation',
          incorporationDate: customer.businessRegistration?.incorporationDate || '',
          incorporationCountry: customer.businessRegistration?.incorporationCountry || ''
        },
        
        // Primary Contact
        primaryContact: {
          name: customer.primaryContact?.name || '',
          title: customer.primaryContact?.title || '',
          email: customer.primaryContact?.email || '',
          phone: customer.primaryContact?.phone || '',
          mobile: customer.primaryContact?.mobile || ''
        },
        
        // Decision Makers
        decisionMakers: customer.decisionMakers || [],
        
        // Addresses
        addresses: {
          headquarters: {
            street: customer.addresses?.headquarters?.street || '',
            city: customer.addresses?.headquarters?.city || '',
            state: customer.addresses?.headquarters?.state || '',
            postalCode: customer.addresses?.headquarters?.postalCode || '',
            country: customer.addresses?.headquarters?.country || ''
          },
          billing: {
            street: customer.addresses?.billing?.street || '',
            city: customer.addresses?.billing?.city || '',
            state: customer.addresses?.billing?.state || '',
            postalCode: customer.addresses?.billing?.postalCode || '',
            country: customer.addresses?.billing?.country || ''
          },
          shipping: {
            street: customer.addresses?.shipping?.street || '',
            city: customer.addresses?.shipping?.city || '',
            state: customer.addresses?.shipping?.state || '',
            postalCode: customer.addresses?.shipping?.postalCode || '',
            country: customer.addresses?.shipping?.country || ''
          }
        },
        
        // Communication
        communication: {
          website: customer.communication?.website || '',
          email: customer.communication?.email || '',
          phone: customer.communication?.phone || '',
          fax: customer.communication?.fax || '',
          preferredMethod: customer.communication?.preferredMethod || 'Email',
          communicationPreferences: {
            marketing: customer.communication?.communicationPreferences?.marketing || true,
            serviceUpdates: customer.communication?.communicationPreferences?.serviceUpdates || true,
            billing: customer.communication?.communicationPreferences?.billing || true,
            emergency: customer.communication?.communicationPreferences?.emergency || true
          }
        },
        
        // Business Details
        businessDetails: {
          creditLimit: customer.businessDetails?.creditLimit || 0,
          paymentTerms: customer.businessDetails?.paymentTerms || 'Net 30',
          currency: customer.businessDetails?.currency || 'USD',
          taxExempt: customer.businessDetails?.taxExempt || false,
          taxExemptionNumber: customer.businessDetails?.taxExemptionNumber || '',
          annualRevenue: customer.businessDetails?.annualRevenue || '',
          employeeCount: customer.businessDetails?.employeeCount || ''
        },
        
        // Customer Value
        customerValue: {
          lifetimeValue: customer.customerValue?.lifetimeValue || 0,
          totalSpent: customer.customerValue?.totalSpent || 0,
          averageOrderValue: customer.customerValue?.averageOrderValue || 0,
          orderFrequency: customer.customerValue?.orderFrequency || 0,
          lastPurchaseDate: customer.customerValue?.lastPurchaseDate || '',
          firstPurchaseDate: customer.customerValue?.firstPurchaseDate || '',
          profitability: customer.customerValue?.profitability || 0
        },
        
        // Satisfaction
        satisfaction: {
          overallRating: customer.satisfaction?.overallRating || 3,
          serviceRating: customer.satisfaction?.serviceRating || 3,
          productRating: customer.satisfaction?.productRating || 3,
          supportRating: customer.satisfaction?.supportRating || 3,
          lastSatisfactionSurvey: customer.satisfaction?.lastSatisfactionSurvey || '',
          feedback: customer.satisfaction?.feedback || '',
          npsScore: customer.satisfaction?.npsScore || ''
        },
        
        // Risk Assessment
        riskAssessment: {
          riskLevel: customer.riskAssessment?.riskLevel || 'Medium',
          creditScore: customer.riskAssessment?.creditScore || '',
          paymentHistory: {
            onTime: customer.riskAssessment?.paymentHistory?.onTime || 0,
            late: customer.riskAssessment?.paymentHistory?.late || 0,
            defaulted: customer.riskAssessment?.paymentHistory?.defaulted || 0
          },
          riskFactors: customer.riskAssessment?.riskFactors || [],
          lastRiskReview: customer.riskAssessment?.lastRiskReview || ''
        },
        
        // Portal Access
        portalAccess: {
          hasAccess: customer.portalAccess?.hasAccess || false,
          username: customer.portalAccess?.username || '',
          lastLogin: customer.portalAccess?.lastLogin || '',
          accessLevel: customer.portalAccess?.accessLevel || 'Basic',
          preferences: {
            theme: customer.portalAccess?.preferences?.theme || 'light',
            language: customer.portalAccess?.preferences?.language || 'en',
            notifications: customer.portalAccess?.preferences?.notifications || true
          }
        },
        
        // Service Locations and Interests
        serviceLocations: customer.serviceLocations || [],
        serviceInterests: customer.serviceInterests || [],
        
        // Status
        status: customer.status || 'Active'
      };
      
      setFormData(initialFormData);
    }
  }, [customer]);

  // Load master data on component mount
  useEffect(() => {
    loadMasterData();
  }, []);

  const loadMasterData = async () => {
    if (!company?._id) return;
    
    setLoadingMasterData(true);
    try {
      const response = await MasterDataService.getContractDropdowns();
      const contractDropdowns = response.data || {};
      
      setMasterData({
        paymentTerms: contractDropdowns.PaymentTerms || [],
        currencies: contractDropdowns.Currency || []
      });
    } catch (error) {
      console.error('Error loading master data:', error);
      // Set fallback data
      setMasterData({
        paymentTerms: [
          { value: 'Net 15', label: 'Net 15' },
          { value: 'Net 30', label: 'Net 30' },
          { value: 'Net 45', label: 'Net 45' },
          { value: 'Net 60', label: 'Net 60' },
          { value: 'Due on Receipt', label: 'Due on Receipt' },
          { value: 'Cash on Delivery', label: 'Cash on Delivery' }
        ],
        currencies: [
          { value: 'USD', label: 'USD - US Dollar' },
          { value: 'EUR', label: 'EUR - Euro' },
          { value: 'GBP', label: 'GBP - British Pound' },
          { value: 'CAD', label: 'CAD - Canadian Dollar' },
          { value: 'AUD', label: 'AUD - Australian Dollar' }
        ]
      });
    } finally {
      setLoadingMasterData(false);
    }
  };

  const generateTestData = () => {
    const timestamp = Date.now();
    const randomId = Math.random().toString(36).substr(2, 9).toUpperCase();
    return {
      // Don't provide customerId - let backend generate it automatically
      name: `TechCorp Solutions Inc. - ${timestamp}-${randomId}`,
      legalName: 'TechCorp Solutions Incorporated',
      tradingName: 'TechCorp',
      customerType: 'Business',
      customerTier: 'Gold',
      industry: 'Technology',
      sector: 'Software',
      businessRegistration: {
        taxId: 'TC-123456789',
        registrationNumber: 'REG-987654321',
        legalEntityType: 'Corporation',
        incorporationDate: '2020-01-15',
        incorporationCountry: 'United States'
      },
      primaryContact: {
        name: 'John Smith',
        title: 'CEO',
        email: 'john.smith@techcorp.com',
        phone: '',
        mobile: ''
      },
      addresses: {
        headquarters: {
          street: '123 Tech Street',
          city: 'San Francisco',
          state: 'CA',
          postalCode: '94105',
          country: 'United States'
        },
        billing: {
          street: '123 Tech Street',
          city: 'San Francisco',
          state: 'CA',
          postalCode: '94105',
          country: 'United States'
        },
        shipping: {
          street: '123 Tech Street',
          city: 'San Francisco',
          state: 'CA',
          postalCode: '94105',
          country: 'United States'
        }
      },
      communication: {
        website: 'https://www.techcorp.com',
        email: 'info@techcorp.com',
        phone: '',
        fax: '+1-555-0124',
        preferredMethod: 'Email',
        communicationPreferences: {
          marketing: true,
          serviceUpdates: true,
          billing: true,
          emergency: true
        }
      },
      businessDetails: {
        creditLimit: 100000,
        paymentTerms: 'Net 30',
        currency: 'USD',
        taxExempt: false,
        taxExemptionNumber: '',
        annualRevenue: '5000000',
        employeeCount: '150'
      },
      customerValue: {
        lifetimeValue: 250000,
        totalSpent: 150000,
        averageOrderValue: 15000,
        orderFrequency: 10,
        lastPurchaseDate: '2024-01-15',
        firstPurchaseDate: '2020-02-01',
        profitability: 85
      },
      satisfaction: {
        overallRating: 4.5,
        serviceRating: 4.7,
        productRating: 4.3,
        supportRating: 4.6,
        lastSatisfactionSurvey: '2024-01-01',
        feedback: 'Excellent service and support. Very satisfied with the products.',
        npsScore: '9'
      },
      riskAssessment: {
        riskLevel: 'Low',
        creditScore: '750',
        paymentHistory: {
          onTime: 95,
          late: 5,
          defaulted: 0
        },
        riskFactors: [],
        lastRiskReview: '2024-01-01'
      },
      portalAccess: {
        hasAccess: true,
        username: 'techcorp_admin',
        lastLogin: '2024-01-20',
        accessLevel: 'Premium',
        preferences: {
          theme: 'light',
          language: 'en',
          notifications: true
        }
      },
      decisionMakers: [
        {
          name: 'Sarah Johnson',
          title: 'VP of Operations',
          role: 'Decision Maker',
          email: 'sarah.johnson@techcorp.com',
          phone: '+1-555-0125',
          isPrimary: true
        },
        {
          name: 'Mike Chen',
          title: 'IT Director',
          role: 'Influencer',
          email: 'mike.chen@techcorp.com',
          phone: '+1-555-0126',
          isPrimary: false
        }
      ],
      serviceLocations: [],
      serviceInterests: [],
      status: 'Active'
    };
  };

  const loadTestData = () => {
    const testData = generateTestData();
    setFormData(testData);
    setTestDataLoaded(true);
    success('Test data loaded successfully!');
  };

  const clearTestData = () => {
    setFormData({
      name: '',
      legalName: '',
      tradingName: '',
      customerType: 'Business',
      customerTier: 'Standard',
      industry: '',
      sector: '',
      businessRegistration: {
        taxId: '',
        registrationNumber: '',
        legalEntityType: 'Corporation',
        incorporationDate: '',
        incorporationCountry: ''
      },
      primaryContact: {
        name: '',
        title: '',
        email: '',
        phone: '',
        mobile: ''
      },
      addresses: {
        headquarters: {
          street: '',
          city: '',
          state: '',
          postalCode: '',
          country: ''
        },
        billing: {
          street: '',
          city: '',
          state: '',
          postalCode: '',
          country: ''
        },
        shipping: {
          street: '',
          city: '',
          state: '',
          postalCode: '',
          country: ''
        }
      },
      communication: {
        website: '',
        email: '',
        phone: '',
        fax: '',
        preferredMethod: 'Email',
        communicationPreferences: {
          marketing: true,
          serviceUpdates: true,
          billing: true,
          emergency: true
        }
      },
      businessDetails: {
        creditLimit: 0,
        paymentTerms: 'Net 30',
        currency: 'USD',
        taxExempt: false,
        taxExemptionNumber: '',
        annualRevenue: '',
        employeeCount: ''
      },
      customerValue: {
        lifetimeValue: 0,
        totalSpent: 0,
        averageOrderValue: 0,
        orderFrequency: 0,
        lastPurchaseDate: '',
        firstPurchaseDate: '',
        profitability: 0
      },
      satisfaction: {
        overallRating: 3,
        serviceRating: 3,
        productRating: 3,
        supportRating: 3,
        lastSatisfactionSurvey: '',
        feedback: '',
        npsScore: ''
      },
      riskAssessment: {
        riskLevel: 'Medium',
        creditScore: '',
        paymentHistory: {
          onTime: 0,
          late: 0,
          defaulted: 0
        },
        riskFactors: [],
        lastRiskReview: ''
      },
      portalAccess: {
        hasAccess: false,
        username: '',
        lastLogin: '',
        accessLevel: 'Basic',
        preferences: {
          theme: 'light',
          language: 'en',
          notifications: true
        }
      },
      status: 'Active'
    });
    setTestDataLoaded(false);
    success('Form cleared successfully!');
  };
  const removeEmptyFieldsDeep = (obj) => {
  if (Array.isArray(obj)) {
    return obj
      .map(removeEmptyFieldsDeep)
      .filter(item => item !== undefined && item !== null);
  }

  if (typeof obj === 'object' && obj !== null) {
    const cleaned = {};
    Object.keys(obj).forEach(key => {
      const value = removeEmptyFieldsDeep(obj[key]);

      if (
        value !== undefined &&
        value !== null &&
        value !== '' &&
        !(typeof value === 'object' && Object.keys(value).length === 0)
      ) {
        cleaned[key] = value;
      }
    });
    return cleaned;
  }
  return obj;
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    if (name.includes('.')) {
      const [parent, child, grandchild, greatGrandchild] = name.split('.');
      setFormData(prev => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: grandchild ? {
            ...prev[parent][child],
            [grandchild]: greatGrandchild ? {
              ...prev[parent][child][grandchild],
              [greatGrandchild]: type === 'checkbox' ? checked : value
            } : (type === 'checkbox' ? checked : value)
          } : (type === 'checkbox' ? checked : value)
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: type === 'checkbox' ? checked : value
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (isSaving) {
      console.log('Already saving, ignoring duplicate submission');
      return; // Prevent double submission
    }
    
    setIsSaving(true);
    console.log('Starting customer save process...');
    
    try {
      // Clean the data before submission
      const cleanedData = {
        ...formData,
        name: formData.name?.trim(),
        legalName: formData.legalName?.trim(),
        tradingName: formData.tradingName?.trim(),
        industry: formData.industry?.trim(),
        sector: formData.sector?.trim(),
        businessRegistration: {
          ...formData.businessRegistration,
          taxId: formData.businessRegistration?.taxId?.trim(),
          registrationNumber: formData.businessRegistration?.registrationNumber?.trim(),
          incorporationCountry: formData.businessRegistration?.incorporationCountry?.trim()
        },
        primaryContact: {
          ...formData.primaryContact,
          name: formData.primaryContact?.name?.trim(),
          title: formData.primaryContact?.title?.trim(),
          email: formData.primaryContact?.email?.trim()
        },
        addresses: {
          ...formData.addresses,
          headquarters: {
            ...formData.addresses?.headquarters,
            street: formData.addresses?.headquarters?.street?.trim(),
            city: formData.addresses?.headquarters?.city?.trim(),
            state: formData.addresses?.headquarters?.state?.trim(),
            postalCode: formData.addresses?.headquarters?.postalCode?.trim(),
            country: formData.addresses?.headquarters?.country?.trim()
          },
          billing: {
            ...formData.addresses?.billing,
            street: formData.addresses?.billing?.street?.trim(),
            city: formData.addresses?.billing?.city?.trim(),
            state: formData.addresses?.billing?.state?.trim(),
            postalCode: formData.addresses?.billing?.postalCode?.trim(),
            country: formData.addresses?.billing?.country?.trim()
          },
          shipping: {
            ...formData.addresses?.shipping,
            street: formData.addresses?.shipping?.street?.trim(),
            city: formData.addresses?.shipping?.city?.trim(),
            state: formData.addresses?.shipping?.state?.trim(),
            postalCode: formData.addresses?.shipping?.postalCode?.trim(),
            country: formData.addresses?.shipping?.country?.trim()
          }
        },
        communication: {
          ...formData.communication,
          website: formData.communication?.website?.trim(),
          email: formData.communication?.email?.trim(),
          fax: formData.communication?.fax?.trim()
        },
        businessDetails: {
          ...formData.businessDetails,
          creditLimit: formData.businessDetails?.creditLimit ? parseFloat(formData.businessDetails.creditLimit) : 0,
          annualRevenue: formData.businessDetails?.annualRevenue ? parseFloat(formData.businessDetails.annualRevenue) : 0,
          employeeCount: formData.businessDetails?.employeeCount ? parseInt(formData.businessDetails.employeeCount) : 0
        },
        customerValue: {
          ...formData.customerValue,
          lifetimeValue: formData.customerValue?.lifetimeValue ? parseFloat(formData.customerValue.lifetimeValue) : 0,
          totalSpent: formData.customerValue?.totalSpent ? parseFloat(formData.customerValue.totalSpent) : 0,
          averageOrderValue: formData.customerValue?.averageOrderValue ? parseFloat(formData.customerValue.averageOrderValue) : 0,
          orderFrequency: formData.customerValue?.orderFrequency ? parseFloat(formData.customerValue.orderFrequency) : 0,
          profitability: formData.customerValue?.profitability ? parseFloat(formData.customerValue.profitability) : 0
        },
        satisfaction: {
          ...formData.satisfaction,
          overallRating: formData.satisfaction?.overallRating ? parseFloat(formData.satisfaction.overallRating) : 3,
          serviceRating: formData.satisfaction?.serviceRating ? parseFloat(formData.satisfaction.serviceRating) : 3,
          productRating: formData.satisfaction?.productRating ? parseFloat(formData.satisfaction.productRating) : 3,
          supportRating: formData.satisfaction?.supportRating ? parseFloat(formData.satisfaction.supportRating) : 3,
          npsScore: formData.satisfaction?.npsScore ? parseInt(formData.satisfaction.npsScore) : 0,
          feedback: formData.satisfaction?.feedback?.trim()
        },
        riskAssessment: {
          ...formData.riskAssessment,
          creditScore: formData.riskAssessment?.creditScore ? parseInt(formData.riskAssessment.creditScore) : undefined,
          paymentHistory: {
            onTime: formData.riskAssessment?.paymentHistory?.onTime ? parseFloat(formData.riskAssessment.paymentHistory.onTime) : 0,
            late: formData.riskAssessment?.paymentHistory?.late ? parseFloat(formData.riskAssessment.paymentHistory.late) : 0,
            defaulted: formData.riskAssessment?.paymentHistory?.defaulted ? parseFloat(formData.riskAssessment.paymentHistory.defaulted) : 0
          }
        }
      };

      // Remove undefined values from nested objects
      Object.keys(cleanedData).forEach(key => {
        if (cleanedData[key] && typeof cleanedData[key] === 'object' && !Array.isArray(cleanedData[key])) {
          Object.keys(cleanedData[key]).forEach(subKey => {
            if (cleanedData[key][subKey] === undefined || cleanedData[key][subKey] === '') {
              delete cleanedData[key][subKey];
            }
          });
        }
      });

      console.log('Cleaned data for submission:', cleanedData);

      let result;
      if (customer) {
        // Update existing customer
        try {
          result = await CustomerService.updateCustomer(customer._id, cleanedData);
          console.log('Customer update result:', result);
          
          // If we get here without an exception, the customer was updated successfully
          success(`Customer "${cleanedData.name}" updated successfully!`);
        } catch (updateError) {
          console.error('Customer update failed:', updateError);
          throw updateError;
        }
      } else {
        // Create new customer
        try {
          const finalPayload = removeEmptyFieldsDeep(cleanedData);
          result = await CustomerService.createCustomer(finalPayload);
          
          console.log('Customer creation result:', result);
          
          // If we get here without an exception, the customer was created successfully
          success(`Customer "${cleanedData.name}" created successfully!`);
        } catch (createError) {
          console.error('Customer creation failed:', createError);
          throw createError;
        }
      }

      // Call the onSave callback if provided
      if (onSave) {
        await onSave(cleanedData);
      }

    } catch (err) {
      console.error('Error saving customer:', err);
      console.error('Full error object:', err);
      console.error('Error response data:', err.response?.data);
      
      // Handle different types of errors
      if (err.response?.data?.errors && Array.isArray(err.response.data.errors)) {
        // Handle validation errors with specific field messages
        const validationErrors = err.response.data.errors;
        console.error('Validation errors:', validationErrors);
        
        // Show individual validation errors as toasts with better formatting
        validationErrors.forEach((validationError, index) => {
          setTimeout(() => {
            const fieldName = validationError.path || validationError.param || 'Field';
            const errorMessage = validationError.msg || validationError.message || 'Invalid value';
            
            // Format field names to be more user-friendly
            const friendlyFieldName = fieldName
              .replace(/([A-Z])/g, ' $1') // Add space before capital letters
              .replace(/^./, str => str.toUpperCase()) // Capitalize first letter
              .replace(/\./g, ' ') // Replace dots with spaces
              .replace(/\b\w/g, l => l.toUpperCase()); // Capitalize each word
            
            error(`${friendlyFieldName}: ${errorMessage}`);
          }, index * 100); // Stagger the toasts
        });
      } else if (err.response?.status === 400) {
        // Handle 400 Bad Request with more specific messages
        const errorMessage = err.response.data?.message || 'Invalid data provided';
        error(`Invalid Input: ${errorMessage}`);
      } else if (err.response?.status === 409) {
        // Handle 409 Conflict (duplicate data)
        error('Customer already exists with this information. Please check for duplicates.');
      } else if (err.response?.status === 401) {
        // Handle 401 Unauthorized
        error('Session expired. Please log in again.');
      } else if (err.response?.status === 403) {
        // Handle 403 Forbidden
        error('You do not have permission to perform this action.');
      } else if (err.response?.status === 404) {
        // Handle 404 Not Found
        error('Customer not found. It may have been deleted.');
      } else if (err.response?.status >= 500) {
        // Handle server errors
        error('Server error occurred. Please try again later or contact support.');
      } else if (err.code === 'NETWORK_ERROR' || err.message?.includes('Network Error')) {
        // Handle network errors
        error('Network connection failed. Please check your internet connection and try again.');
      } else if (err.response?.data?.message) {
        // Show backend error message
        error(err.response.data.message);
      } else if (err.message) {
        // Show general error message
        error(`Error: ${err.message}`);
      } else {
        // Fallback error message
        error('Failed to save customer. Please try again.');
      }
    } finally {
      setIsSaving(false);
    }
  };

  const addDecisionMaker = () => {
    if (newDecisionMaker.name) {
      setFormData(prev => ({
        ...prev,
        decisionMakers: [...prev.decisionMakers, newDecisionMaker]
      }));
      setNewDecisionMaker({
        name: '',
        title: '',
        role: 'Decision Maker',
        email: '',
        phone: '',
        isPrimary: false
      });
    }
  };

  const removeDecisionMaker = (index) => {
    setFormData(prev => ({
      ...prev,
      decisionMakers: prev.decisionMakers.filter((_, i) => i !== index)
    }));
  };

  const addServiceLocation = () => {
    if (newServiceLocation.name) {
      setFormData(prev => ({
        ...prev,
        serviceLocations: [...prev.serviceLocations, newServiceLocation]
      }));
      setNewServiceLocation({
        name: '',
        address: {
          street: '',
          city: '',
          state: '',
          postalCode: '',
          country: ''
        },
        contactPerson: {
          name: '',
          phone: '',
          email: ''
        },
        isPrimary: false
      });
    }
  };

  const removeServiceLocation = (index) => {
    setFormData(prev => ({
      ...prev,
      serviceLocations: prev.serviceLocations.filter((_, i) => i !== index)
    }));
  };

  const addServiceInterest = () => {
    if (newServiceInterest.category) {
      setFormData(prev => ({
        ...prev,
        serviceInterests: [...prev.serviceInterests, newServiceInterest]
      }));
      setNewServiceInterest({
        category: '',
        subcategory: '',
        priority: 'Medium',
        budget: '',
        timeline: ''
      });
    }
  };

  const removeServiceInterest = (index) => {
    setFormData(prev => ({
      ...prev,
      serviceInterests: prev.serviceInterests.filter((_, i) => i !== index)
    }));
  };

  const addRiskFactor = () => {
    if (newRiskFactor.factor) {
      setFormData(prev => ({
        ...prev,
        riskAssessment: {
          ...prev.riskAssessment,
          riskFactors: [...prev.riskAssessment.riskFactors, newRiskFactor]
        }
      }));
      setNewRiskFactor({
        factor: '',
        impact: 'Medium',
        mitigation: ''
      });
    }
  };

  const removeRiskFactor = (index) => {
    setFormData(prev => ({
      ...prev,
      riskAssessment: {
        ...prev.riskAssessment,
        riskFactors: prev.riskAssessment.riskFactors.filter((_, i) => i !== index)
      }
    }));
  };

  const tabs = [
    { id: 'basic', label: 'Basic Info', icon: Users },
    { id: 'contact', label: 'Contact', icon: Phone },
    { id: 'business', label: 'Business', icon: CreditCard },
    { id: 'value', label: 'Value', icon: Star },
    { id: 'satisfaction', label: 'Satisfaction', icon: Heart }
  ];

  // Helper function to get form control props based on view mode
  const getFormControlProps = (type = 'text') => {
    const baseStyle = {
      borderRadius: '6px',
      border: '1px solid #d1d5db',
      padding: '8px 12px',
      fontSize: '14px',
      width: '100%',
      boxSizing: 'border-box',
      fontFamily: 'Inter, sans-serif'
    };

    if (viewMode) {
      return {
        readOnly: true,
        disabled: true,
        className: 'form-control',
        style: {
          ...baseStyle,
          backgroundColor: '#f3f4f6',
          color: '#1f2937',
          cursor: 'not-allowed'
        }
      };
    }

    return {
      className: 'form-control',
      style: baseStyle
    };
  };

  const renderBasicInfo = () => (
    <div className="form-row" style={{ display: 'flex', gap: '12px', width: '100%', flexWrap: 'wrap' }}>
      <div style={{ flex: '1', minWidth: '300px' }}>
        <div className="form-group" style={{ width: '100%' }}>
          <label className="form-label required" style={{ fontWeight: '600', color: '#374151', marginBottom: '6px', fontSize: '13px' }}>
            Customer Name
          </label>
          <input
            type="text"
            name="name"
            value={formData.name}
            onChange={handleChange}
            required={!viewMode}
            placeholder="Enter customer name"
            {...getFormControlProps('text')}
          />
        </div>
      </div>
      <div style={{ flex: '1', minWidth: '300px' }}>
        <div className="form-group" style={{ width: '100%' }}>
          <label className="form-label" style={{ fontWeight: '600', color: '#374151', marginBottom: '6px', fontSize: '13px' }}>
            Legal Name
          </label>
          <input
            type="text"
            name="legalName"
            value={formData.legalName}
            onChange={handleChange}
            placeholder="Enter legal name"
            {...getFormControlProps('text')}
          />
        </div>
      </div>
      <div style={{ flex: '1', minWidth: '300px' }}>
        <div className="form-group" style={{ width: '100%' }}>
          <label className="form-label" style={{ fontWeight: '600', color: '#374151', marginBottom: '6px', fontSize: '13px' }}>
            Trading Name
          </label>
          <input
            type="text"
            name="tradingName"
            value={formData.tradingName}
            onChange={handleChange}
            placeholder="Enter trading name"
            {...getFormControlProps('text')}
          />
        </div>
      </div>
      <div style={{ flex: '1', minWidth: '300px' }}>
        <div className="form-group" style={{ width: '100%' }}>
          <label className="form-label required" style={{ fontWeight: '600', color: '#374151', marginBottom: '6px', fontSize: '13px' }}>
            Customer Type
          </label>
          <select
            name="customerType"
            value={formData.customerType}
            onChange={handleChange}
            required={!viewMode}
            {...getFormControlProps('select')}
          >
            <option value="Individual">Individual</option>
            <option value="Business">Business</option>
            <option value="Government">Government</option>
            <option value="Non-Profit">Non-Profit</option>
            <option value="Educational">Educational</option>
            <option value="Healthcare">Healthcare</option>
            <option value="Other">Other</option>
          </select>
        </div>
      </div>
      <div style={{ flex: '1', minWidth: '300px' }}>
        <div className="form-group" style={{ width: '100%' }}>
          <label className="form-label" style={{ fontWeight: '600', color: '#374151', marginBottom: '6px', fontSize: '13px' }}>
            Customer Tier
          </label>
          <select
            name="customerTier"
            value={formData.customerTier}
            onChange={handleChange}
            {...getFormControlProps('select')}
          >
            <option value="Platinum">Platinum</option>
            <option value="Gold">Gold</option>
            <option value="Silver">Silver</option>
            <option value="Bronze">Bronze</option>
            <option value="Standard">Standard</option>
          </select>
        </div>
      </div>
      <div style={{ flex: '1', minWidth: '300px' }}>
        <div className="form-group" style={{ width: '100%' }}>
          <label className="form-label" style={{ fontWeight: '600', color: '#374151', marginBottom: '6px', fontSize: '13px' }}>
            Industry
          </label>
          <input
            type="text"
            name="industry"
            value={formData.industry}
            onChange={handleChange}
            placeholder="e.g., Technology, Manufacturing"
            {...getFormControlProps('text')}
          />
        </div>
      </div>
      <div style={{ flex: '1', minWidth: '300px' }}>
        <div className="form-group" style={{ width: '100%' }}>
          <label className="form-label" style={{ fontWeight: '600', color: '#374151', marginBottom: '6px', fontSize: '13px' }}>
            Status
          </label>
          <select
            name="status"
            value={formData.status}
            onChange={handleChange}
            {...getFormControlProps('select')}
          >
            <option value="Active">Active</option>
            <option value="Inactive">Inactive</option>
            <option value="Suspended">Suspended</option>
            <option value="Prospect">Prospect</option>
            <option value="Lead">Lead</option>
            <option value="Converted">Converted</option>
          </select>
        </div>
      </div>
    </div>
  );

  const renderContactInfo = () => (
    <div className="form-row" style={{ display: 'flex', gap: '12px', width: '100%', flexWrap: 'wrap' }}>
      <div style={{ flex: '1', minWidth: '300px' }}>
        <div className="form-group" style={{ width: '100%' }}>
          <label className="form-label" style={{ fontWeight: '600', color: '#374151', marginBottom: '6px', fontSize: '13px' }}>
            Contact Name
          </label>
          <input
            type="text"
            name="primaryContact.name"
            value={formData.primaryContact.name}
            onChange={handleChange}
            placeholder="Enter contact name"
            {...getFormControlProps('text')}
          />
        </div>
      </div>
      <div style={{ flex: '1', minWidth: '300px' }}>
        <div className="form-group" style={{ width: '100%' }}>
          <label className="form-label" style={{ fontWeight: '600', color: '#374151', marginBottom: '6px', fontSize: '13px' }}>
            Title
          </label>
          <input
            type="text"
            name="primaryContact.title"
            value={formData.primaryContact.title}
            onChange={handleChange}
            placeholder="Enter job title"
            {...getFormControlProps('text')}
          />
        </div>
      </div>
      <div style={{ flex: '1', minWidth: '300px' }}>
        <div className="form-group" style={{ width: '100%' }}>
          <label className="form-label" style={{ fontWeight: '600', color: '#374151', marginBottom: '6px', fontSize: '13px' }}>
            Email
          </label>
          <input
            type="email"
            name="primaryContact.email"
            value={formData.primaryContact.email}
            onChange={handleChange}
            placeholder="Enter email address"
            {...getFormControlProps('email')}
          />
        </div>
      </div>
      <div style={{ flex: '1', minWidth: '300px' }}>
        <div className="form-group" style={{ width: '100%' }}>
          <label className="form-label" style={{ fontWeight: '600', color: '#374151', marginBottom: '6px', fontSize: '13px' }}>
            Phone
          </label>
          <input
            type="tel"
            name="primaryContact.phone"
            value={formData.primaryContact.phone}
            onChange={handleChange}
            placeholder="Enter phone number"
            {...getFormControlProps('tel')}
          />
        </div>
      </div>
      
      {/* Decision Makers */}
      <div className="col-12">
        <h6 className="mb-3 mt-4">Decision Makers</h6>
        {formData.decisionMakers.map((maker, index) => (
          <div key={index} className="card mb-2">
            <div className="card-body">
              <div className="row">
                <div className="col-md-3">
                  <strong>{maker.name}</strong>
                  {maker.isPrimary && <span className="badge bg-primary ms-2">Primary</span>}
                </div>
                <div className="col-md-2">{maker.title}</div>
                <div className="col-md-2">{maker.role}</div>
                <div className="col-md-3">{maker.email}</div>
                <div className="col-md-2">
                  {!viewMode && (
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-danger"
                      onClick={() => removeDecisionMaker(index)}
                    >
                      Remove
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        ))}
        {!viewMode && (
          <div className="card">
            <div className="card-body">
              <div className="form-row" style={{ display: 'flex', gap: '12px', width: '100%', flexWrap: 'wrap' }}>
                {/* First Row: Name and Title */}
                <div style={{ flex: '1', minWidth: '300px' }}>
                  <div className="form-group" style={{ width: '100%' }}>
                    <label className="form-label" style={{ fontWeight: '600', color: '#374151', marginBottom: '6px', fontSize: '13px' }}>
                      Name
                    </label>
                    <input
                      type="text"
                      placeholder="Enter name"
                      value={newDecisionMaker.name}
                      onChange={(e) => setNewDecisionMaker(prev => ({ ...prev, name: e.target.value }))}
                      {...getFormControlProps('text')}
                    />
                  </div>
                </div>
                <div style={{ flex: '1', minWidth: '300px' }}>
                  <div className="form-group" style={{ width: '100%' }}>
                    <label className="form-label" style={{ fontWeight: '600', color: '#374151', marginBottom: '6px', fontSize: '13px' }}>
                      Title
                    </label>
                    <input
                      type="text"
                      placeholder="Enter title"
                      value={newDecisionMaker.title}
                      onChange={(e) => setNewDecisionMaker(prev => ({ ...prev, title: e.target.value }))}
                      {...getFormControlProps('text')}
                    />
                  </div>
                </div>
                
                {/* Second Row: Role and Email */}
                <div style={{ flex: '1', minWidth: '300px' }}>
                  <div className="form-group" style={{ width: '100%' }}>
                    <label className="form-label" style={{ fontWeight: '600', color: '#374151', marginBottom: '6px', fontSize: '13px' }}>
                      Role
                    </label>
                    <select
                      value={newDecisionMaker.role}
                      onChange={(e) => setNewDecisionMaker(prev => ({ ...prev, role: e.target.value }))}
                      {...getFormControlProps('select')}
                    >
                      <option value="Decision Maker">Decision Maker</option>
                      <option value="Influencer">Influencer</option>
                      <option value="User">User</option>
                      <option value="Gatekeeper">Gatekeeper</option>
                      <option value="Buyer">Buyer</option>
                    </select>
                  </div>
                </div>
                <div style={{ flex: '1', minWidth: '300px' }}>
                  <div className="form-group" style={{ width: '100%' }}>
                    <label className="form-label" style={{ fontWeight: '600', color: '#374151', marginBottom: '6px', fontSize: '13px' }}>
                      Email
                    </label>
                    <input
                      type="email"
                      placeholder="Enter email"
                      value={newDecisionMaker.email}
                      onChange={(e) => setNewDecisionMaker(prev => ({ ...prev, email: e.target.value }))}
                      {...getFormControlProps('email')}
                    />
                  </div>
                </div>
                
                {/* Add Button */}
                <div style={{ flex: '1', minWidth: '300px' }}>
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={addDecisionMaker}
                  >
                    Add Decision Maker
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );

  const renderBusinessInfo = () => (
    <div className="form-row" style={{ display: 'flex', gap: '12px', width: '100%', flexWrap: 'wrap' }}>
      <div style={{ flex: '1', minWidth: '300px' }}>
        <div className="form-group" style={{ width: '100%' }}>
          <label className="form-label" style={{ fontWeight: '600', color: '#374151', marginBottom: '6px', fontSize: '13px' }}>
            Tax ID
          </label>
          <input
            type="text"
            name="businessRegistration.taxId"
            value={formData.businessRegistration.taxId}
            onChange={handleChange}
            placeholder="Enter tax ID"
            {...getFormControlProps('text')}
          />
        </div>
      </div>
      <div style={{ flex: '1', minWidth: '300px' }}>
        <div className="form-group" style={{ width: '100%' }}>
          <label className="form-label" style={{ fontWeight: '600', color: '#374151', marginBottom: '6px', fontSize: '13px' }}>
            Registration Number
          </label>
          <input
            type="text"
            name="businessRegistration.registrationNumber"
            value={formData.businessRegistration.registrationNumber}
            onChange={handleChange}
            placeholder="Enter registration number"
            {...getFormControlProps('text')}
          />
        </div>
      </div>
      <div style={{ flex: '1', minWidth: '300px' }}>
        <div className="form-group" style={{ width: '100%' }}>
          <label className="form-label" style={{ fontWeight: '600', color: '#374151', marginBottom: '6px', fontSize: '13px' }}>
            Credit Limit
          </label>
          <input
            type="number"
            name="businessDetails.creditLimit"
            value={formData.businessDetails.creditLimit}
            onChange={handleChange}
            placeholder="Enter credit limit"
            {...getFormControlProps('number')}
          />
        </div>
      </div>
      <div style={{ flex: '1', minWidth: '300px' }}>
        <div className="form-group" style={{ width: '100%' }}>
          <label className="form-label" style={{ fontWeight: '600', color: '#374151', marginBottom: '6px', fontSize: '13px' }}>
            Payment Terms
          </label>
          <select
            name="businessDetails.paymentTerms"
            value={formData.businessDetails.paymentTerms}
            onChange={handleChange}
            {...getFormControlProps('select')}
          >
            <option value="">Select Payment Terms</option>
            {masterData.paymentTerms.map((term, index) => (
              <option key={index} value={term.value || term}>
                {term.label || term}
              </option>
            ))}
          </select>
        </div>
      </div>
      <div style={{ flex: '1', minWidth: '300px' }}>
        <div className="form-group" style={{ width: '100%' }}>
          <label className="form-label" style={{ fontWeight: '600', color: '#374151', marginBottom: '6px', fontSize: '13px' }}>
            Currency
          </label>
          <select
            name="businessDetails.currency"
            value={formData.businessDetails.currency}
            onChange={handleChange}
            {...getFormControlProps('select')}
          >
            <option value="">Select Currency</option>
            {masterData.currencies.map((currency, index) => (
              <option key={index} value={currency.value || currency}>
                {currency.label || currency}
              </option>
            ))}
          </select>
        </div>
      </div>
      <div style={{ flex: '1', minWidth: '300px' }}>
        <div className="form-group" style={{ width: '100%' }}>
          <label className="form-label" style={{ fontWeight: '600', color: '#374151', marginBottom: '6px', fontSize: '13px' }}>
            Annual Revenue
          </label>
          <input
            type="number"
            name="businessDetails.annualRevenue"
            value={formData.businessDetails.annualRevenue}
            onChange={handleChange}
            placeholder="Enter annual revenue"
            {...getFormControlProps('number')}
          />
        </div>
      </div>
      <div style={{ flex: '1', minWidth: '300px' }}>
        <div className="form-group" style={{ width: '100%' }}>
          <label className="form-label" style={{ fontWeight: '600', color: '#374151', marginBottom: '6px', fontSize: '13px' }}>
            Employee Count
          </label>
          <input
            type="number"
            name="businessDetails.employeeCount"
            value={formData.businessDetails.employeeCount}
            onChange={handleChange}
            placeholder="Enter employee count"
            {...getFormControlProps('number')}
          />
        </div>
      </div>
    </div>
  );

  const renderValueInfo = () => (
    <div className="form-row" style={{ display: 'flex', gap: '12px', width: '100%', flexWrap: 'wrap' }}>
      <div style={{ flex: '1', minWidth: '300px' }}>
        <div className="form-group" style={{ width: '100%' }}>
          <label className="form-label" style={{ fontWeight: '600', color: '#374151', marginBottom: '6px', fontSize: '13px' }}>
            Lifetime Value
          </label>
          <input
            type="number"
            name="customerValue.lifetimeValue"
            value={formData.customerValue.lifetimeValue}
            onChange={handleChange}
            placeholder="Enter lifetime value"
            {...getFormControlProps('number')}
          />
        </div>
      </div>
      <div style={{ flex: '1', minWidth: '300px' }}>
        <div className="form-group" style={{ width: '100%' }}>
          <label className="form-label" style={{ fontWeight: '600', color: '#374151', marginBottom: '6px', fontSize: '13px' }}>
            Total Spent
          </label>
          <input
            type="number"
            name="customerValue.totalSpent"
            value={formData.customerValue.totalSpent}
            onChange={handleChange}
            placeholder="Enter total spent"
            {...getFormControlProps('number')}
          />
        </div>
      </div>
      <div style={{ flex: '1', minWidth: '300px' }}>
        <div className="form-group" style={{ width: '100%' }}>
          <label className="form-label" style={{ fontWeight: '600', color: '#374151', marginBottom: '6px', fontSize: '13px' }}>
            Average Order Value
          </label>
          <input
            type="number"
            name="customerValue.averageOrderValue"
            value={formData.customerValue.averageOrderValue}
            onChange={handleChange}
            placeholder="Enter average order value"
            {...getFormControlProps('number')}
          />
        </div>
      </div>
      <div style={{ flex: '1', minWidth: '300px' }}>
        <div className="form-group" style={{ width: '100%' }}>
          <label className="form-label" style={{ fontWeight: '600', color: '#374151', marginBottom: '6px', fontSize: '13px' }}>
            Order Frequency
          </label>
          <input
            type="number"
            name="customerValue.orderFrequency"
            value={formData.customerValue.orderFrequency}
            onChange={handleChange}
            placeholder="Enter order frequency"
            {...getFormControlProps('number')}
          />
        </div>
      </div>
      <div style={{ flex: '1', minWidth: '300px' }}>
        <div className="form-group" style={{ width: '100%' }}>
          <label className="form-label" style={{ fontWeight: '600', color: '#374151', marginBottom: '6px', fontSize: '13px' }}>
            Last Purchase Date
          </label>
          <input
            type="date"
            name="customerValue.lastPurchaseDate"
            value={formData.customerValue.lastPurchaseDate}
            onChange={handleChange}
            {...getFormControlProps('date')}
          />
        </div>
      </div>
      <div style={{ flex: '1', minWidth: '300px' }}>
        <div className="form-group" style={{ width: '100%' }}>
          <label className="form-label" style={{ fontWeight: '600', color: '#374151', marginBottom: '6px', fontSize: '13px' }}>
            First Purchase Date
          </label>
          <input
            type="date"
            name="customerValue.firstPurchaseDate"
            value={formData.customerValue.firstPurchaseDate}
            onChange={handleChange}
            {...getFormControlProps('date')}
          />
        </div>
      </div>
    </div>
  );

  const renderSatisfactionInfo = () => (
    <div className="form-row" style={{ display: 'flex', gap: '12px', width: '100%', flexWrap: 'wrap' }}>
      <div style={{ flex: '1', minWidth: '300px' }}>
        <div className="form-group" style={{ width: '100%' }}>
          <label className="form-label" style={{ fontWeight: '600', color: '#374151', marginBottom: '6px', fontSize: '13px' }}>
            Overall Rating
          </label>
          <input
            type="number"
            min="1"
            max="5"
            step="0.1"
            name="satisfaction.overallRating"
            value={formData.satisfaction.overallRating}
            onChange={handleChange}
            placeholder="1.0 - 5.0"
            {...getFormControlProps('number')}
          />
        </div>
      </div>
      <div style={{ flex: '1', minWidth: '300px' }}>
        <div className="form-group" style={{ width: '100%' }}>
          <label className="form-label" style={{ fontWeight: '600', color: '#374151', marginBottom: '6px', fontSize: '13px' }}>
            Service Rating
          </label>
          <input
            type="number"
            min="1"
            max="5"
            step="0.1"
            name="satisfaction.serviceRating"
            value={formData.satisfaction.serviceRating}
            onChange={handleChange}
            placeholder="1.0 - 5.0"
            {...getFormControlProps('number')}
          />
        </div>
      </div>
      <div style={{ flex: '1', minWidth: '300px' }}>
        <div className="form-group" style={{ width: '100%' }}>
          <label className="form-label" style={{ fontWeight: '600', color: '#374151', marginBottom: '6px', fontSize: '13px' }}>
            Product Rating
          </label>
          <input
            type="number"
            min="1"
            max="5"
            step="0.1"
            name="satisfaction.productRating"
            value={formData.satisfaction.productRating}
            onChange={handleChange}
            placeholder="1.0 - 5.0"
            {...getFormControlProps('number')}
          />
        </div>
      </div>
      <div style={{ flex: '1', minWidth: '300px' }}>
        <div className="form-group" style={{ width: '100%' }}>
          <label className="form-label" style={{ fontWeight: '600', color: '#374151', marginBottom: '6px', fontSize: '13px' }}>
            Support Rating
          </label>
          <input
            type="number"
            min="1"
            max="5"
            step="0.1"
            name="satisfaction.supportRating"
            value={formData.satisfaction.supportRating}
            onChange={handleChange}
            placeholder="1.0 - 5.0"
            {...getFormControlProps('number')}
          />
        </div>
      </div>
      <div style={{ flex: '1', minWidth: '300px' }}>
        <div className="form-group" style={{ width: '100%' }}>
          <label className="form-label" style={{ fontWeight: '600', color: '#374151', marginBottom: '6px', fontSize: '13px' }}>
            NPS Score
          </label>
          <input
            type="number"
            min="0"
            max="10"
            name="satisfaction.npsScore"
            value={formData.satisfaction.npsScore}
            onChange={handleChange}
            placeholder="0 - 10"
            {...getFormControlProps('number')}
          />
        </div>
      </div>
      <div style={{ flex: '1', minWidth: '300px' }}>
        <div className="form-group" style={{ width: '100%' }}>
          <label className="form-label" style={{ fontWeight: '600', color: '#374151', marginBottom: '6px', fontSize: '13px' }}>
            Feedback
          </label>
          <textarea
            name="satisfaction.feedback"
            rows="4"
            value={formData.satisfaction.feedback}
            onChange={handleChange}
            placeholder="Enter customer feedback"
            {...getFormControlProps('textarea')}
          />
        </div>
      </div>
    </div>
  );

  const renderTabContent = () => {
    switch (activeTab) {
      case 'basic':
        return renderBasicInfo();
      case 'contact':
        return renderContactInfo();
      case 'business':
        return renderBusinessInfo();
      case 'value':
        return renderValueInfo();
      case 'satisfaction':
        return renderSatisfactionInfo();
      default:
        return renderBasicInfo();
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      {/* Tab Navigation */}
      <div className="tabs-modern">
        <ul className="tabs-modern-list">
          {tabs.map(tab => (
            <li key={tab.id} className="tab-modern-item">
              <button
                type="button"
                className={`tab-modern-link ${activeTab === tab.id ? 'active' : ''}`}
                onClick={() => setActiveTab(tab.id)}
              >
                <tab.icon size={16} className="tab-modern-icon" />
                {tab.label}
              </button>
            </li>
          ))}
        </ul>
      </div>

      {/* Tab Content */}
      <div className="tab-content">
        {renderTabContent()}
      </div>

      {/* Main Action Buttons */}
      <div className="d-flex gap-2">
        <button
          type="button"
          className="btn btn-outline-secondary d-flex align-items-center gap-2"
          onClick={onCancel}
          disabled={isSaving}
          style={{ fontFamily: 'Inter, sans-serif' }}
          >
          <X size={16} />
            Cancel
        </button>
        {!viewMode && (
          <>
            {/* Test Data Buttons – ONLY for Create */}
              {!customer && (
                <div className="d-flex gap-2 me-auto">
                  <button
                    type="button"
                    className="btn btn-outline-info btn-sm"
                    onClick={loadTestData}
                    disabled={isSaving}
                  >Load Test Data
          </button>
          <button
              type="button"
              className="btn btn-outline-warning btn-sm"
              onClick={clearTestData}
              disabled={isSaving}
              >Clear
          </button>
        </div>
      )}

    {/* Action Buttons – Create & Edit */}
    <div className="d-flex gap-2 ms-auto">
      <button
        type="button"
        className="btn btn-outline-secondary"
        onClick={onCancel}
        disabled={isSaving}
      >
        Cancel
      </button>

      <button
        type="submit"
        className="btn btn-primary"
        disabled={isSaving}
      >
        {isSaving
          ? 'Saving...'
          : customer
          ? 'Update Customer'
          : 'Create Customer'}
      </button>
    </div>
  </>
)}

        {viewMode && (
          <div className="d-flex gap-2 ms-auto">
            <button
              type="button"
              className="btn btn-secondary d-flex align-items-center gap-2"
              onClick={onCancel}
              style={{ fontFamily: 'Inter, sans-serif' }}
            >
              <X size={16} />
              Close
            </button>
          </div>
        )}
      </div>
      
      {/* Toast Container */}
      <ToastContainer toasts={toasts} removeToast={removeToast} />
    </form>
  );
};

export default CustomerForm;
